import { DT } from "../../constants.ts";
import { add, scale, viewDir } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, MoveKind, setHv } from "../../sim/components.ts";
import { aim, applyDamage, applyStatus, explode, hitscan, isEnemy, playersInRadius, spawnZone } from "../../sim/combat.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, pk, zk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, glide, launchProjectile, ownerAlive, ready, secs, simpleProjectile, startMove, toGround, trigger, useUlt } from "../kitlib.ts";

export const V = { flock: 0, ultShot: 1, lastCrow: 2 } as const;
export const PK = { Piercer: pk(HeroId.Hella, 0), Soul: pk(HeroId.Hella, 1), DeathCrow: pk(HeroId.Hella, 2) } as const;
export const ZK = { Thorn: zk(HeroId.Hella, 0), Crowstorm: zk(HeroId.Hella, 1) } as const;
export const FX = { Thorn: fxk(HeroId.Hella, 0), Hel: fxk(HeroId.Hella, 1), Crow: fxk(HeroId.Hella, 2) } as const;
const HERO_MOVE_FLOCK = MoveKind.Hero;

const def: HeroDef = {
  id: HeroId.Hella,
  slug: "hella",
  name: "Hella",
  role: Role.Damage,
  color: "#2e8b57",
  blurb: "Precision death-queen with headshot blades, brief crow flight, a stun orb, and an airborne bombardment ultimate.",
  health: 250,
  armor: 0,
  shields: 0,
  radius: 0.4,
  height: 1.85,
  headRadius: 0.23,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 10,
  reloadTime: 1.5,
  ultCost: 4000,
  abilities: {
    [Slot.Primary]: { name: "Night Pins", key: "LMB", description: "Throw precise hitscan thorns. Headshots hit hard.", cooldown: 0 },
    [Slot.Secondary]: { name: "Pinned Midnight", key: "RMB", description: "Launch four thorns that stick, then explode after 3 seconds.", cooldown: 8 },
    [Slot.Ability1]: { name: "Grave Flock", key: "Shift", description: "Become an invulnerable crow and surge up to 18 meters.", cooldown: 15 },
    [Slot.Ability2]: { name: "Soul Pinch", key: "E", description: "Throw an arcing orb that pulls, damages, and briefly stuns enemies.", cooldown: 10 },
    [Slot.Ultimate]: { name: "Crownfall", key: "Q", description: "Rise up with bonus health and rain explosive crows for 10 seconds.", cooldown: 0 },
  },
  passives: [
    { name: "Feathered Descent", description: "Hold Space while airborne to slow your fall." },
    { name: "After-Crow", description: "Final blows leave a delayed crow blast at the victim." },
  ],
};

function detonate(sim: Parameters<HeroModule["kit"]["tick"]>[0], owner: number, pos: { x: number; y: number; z: number }, damage: number, radius: number, kind: number, minDamage = damage * 0.45) {
  if (!ownerAlive(sim, owner)) return;
  explode(sim, owner, pos, { radius, damage, minDamage, fxKind: kind });
}

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.flock, 0);
      setHv(sim.c, eid, V.ultShot, 0);
      setHv(sim.c, eid, V.lastCrow, 0);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      setHv(c, eid, V.flock, c.Move.kind[eid] === HERO_MOVE_FLOCK && c.Move.ticks[eid]! > 0 ? 1 : 0);
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(10);
        c.Health.over[eid] = Math.max(c.Health.over[eid]!, 1000);
        c.Health.overTicks[eid] = Math.max(c.Health.overTicks[eid]!, secs(10));
        action(sim, eid, ActionKind.Ult, 24);
      }
      if (c.Ult.active[eid]! > 0) {
        c.Vel.y[eid] = Math.max(c.Vel.y[eid]!, 2.5);
        if (trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) {
          c.Weapon.fireCd[eid] = 60;
          action(sim, eid, ActionKind.Ult, 12);
          launchProjectile(sim, eid, PK.DeathCrow, 80, input, { radius: 0.28, seconds: 2.4 });
          setHv(c, eid, V.ultShot, (hv(c, eid, V.ultShot) + 1) % 256);
        }
        return;
      }

      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        applyStatus(sim, eid, "invuln", 1.2, { source: eid });
        const d = viewDir(input.yaw, Math.max(-0.15, Math.min(0.3, input.pitch)));
        startMove(sim, eid, HERO_MOVE_FLOCK, secs(1.2), 15, { x: d.x * 15, y: d.y * 15, z: d.z * 15 });
        action(sim, eid, ActionKind.Cast, 20);
      }
      if (pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        action(sim, eid, ActionKind.AltFire, 18);
        const a = aim(sim, eid);
        for (let i = 0; i < 4; i++) {
          const side = (i - 1.5) * 0.035;
          launchProjectile(sim, eid, PK.Piercer, 80, input, { radius: 0.1, seconds: 1.4, dir: { x: a.dir.x + side, y: a.dir.y, z: a.dir.z } });
        }
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 18);
        launchProjectile(sim, eid, PK.Soul, 40, input, { radius: 0.25, gravity: 8, seconds: 2.2 });
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
        c.Weapon.fireCd[eid] = 30;
        action(sim, eid, ActionKind.Fire, 10);
        const a = aim(sim, eid);
        hitscan(sim, eid, a.origin, a.dir, { range: 70, damage: 80, falloffStart: 18, falloffEnd: 35, falloffMin: 0.75, canCrit: true, fxKind: FX.Thorn });
      }
    },
    movement(sim, eid, input, mods) {
      if (sim.c.Ult.active[eid]! > 0) {
        mods.gravityMul = 0;
        mods.speedMul *= 0.5;
      } else if (input.buttons & Btn.Jump) glide(sim, eid, input, mods, { fallSpeed: 3.5, driftSpeed: 6 });
    },
    scripted(sim, eid, _input, kind) {
      if (kind !== HERO_MOVE_FLOCK) return;
      sim.c.Vel.x[eid] = sim.c.Move.x[eid]!;
      sim.c.Vel.y[eid] = sim.c.Move.y[eid]!;
      sim.c.Vel.z[eid] = sim.c.Move.z[eid]!;
    },
    onDealtDamage(sim, attacker, target, dealt) {
      if (!sim.authority || dealt <= 0 || sim.c.Health.hp[target]! > 0.001 || !ownerAlive(sim, attacker)) return;
      const last = hv(sim.c, attacker, V.lastCrow);
      if (sim.tick - last < 6) return;
      setHv(sim.c, attacker, V.lastCrow, sim.tick);
      spawnZone(sim, attacker, ZK.Crowstorm, toGround(sim, add(sim.posOf(target), { x: 0, y: 0.5, z: 0 })), 5, 1.8);
    },
  },
  projectiles: {
    [PK.Piercer]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        if (hit.target !== undefined) applyDamage(sim, hit.target, 10, owner, { pos: hit.pos, info: { kind: "projectile", sourceKind: PK.Piercer } });
        spawnZone(sim, owner, ZK.Thorn, toGround(sim, hit.pos), 2.4, 3);
      },
      { color: "#91ffbd", size: 0.1, trail: true },
    ),
    [PK.Soul]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        sim.emit({ type: "explosion", pos: hit.pos, radius: 3, kind: FX.Hel });
        for (const { eid: t } of playersInRadius(sim, hit.pos, 3, (e) => isEnemy(sim, owner, e), false)) {
          applyDamage(sim, t, 40, owner, { pos: hit.pos, info: { kind: "explosion", sourceKind: PK.Soul } });
          applyStatus(sim, t, "stun", 0.3, { source: owner });
          const p = sim.posOf(t);
          sim.c.Ext.x[t] += (hit.pos.x - p.x) * 2.2;
          sim.c.Ext.z[t] += (hit.pos.z - p.z) * 2.2;
        }
      },
      { color: "#7affb2", size: 0.24, trail: true },
    ),
    [PK.DeathCrow]: simpleProjectile(
      (sim, _p, owner, hit) => detonate(sim, owner, hit.pos, 125, 6, FX.Crow, 0),
      { color: "#b5ffd0", size: 0.28, trail: true },
    ),
  },
  zones: {
    [ZK.Thorn]: {
      visual: { color: "#91ffbd", dome: false },
      tick() {},
      end(sim, z) {
        detonate(sim, sim.c.Zone.owner[z]!, sim.posOf(z), 35, sim.c.Zone.radius[z]!, FX.Thorn);
      },
    },
    [ZK.Crowstorm]: {
      visual: { color: "#2e8b57", dome: true },
      tick() {},
      end(sim, z) {
        detonate(sim, sim.c.Zone.owner[z]!, sim.posOf(z), 80, sim.c.Zone.radius[z]!, FX.Crow);
      },
    },
  },
  fx: {
    [FX.Thorn]: { color: "#91ffbd" },
    [FX.Hel]: { color: "#45ff98" },
    [FX.Crow]: { color: "#c7ffd9" },
  },
};

void DT;
void scale;
export default module;
